import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { gameService, type SpinAction } from '@/services/game/game.service'
import {
  assertPersonalSession,
  isCurrentPersonalSession,
  requirePersonalSession,
  type PersonalSession,
} from '../personal-session'

export function useSpinMutation() {
  const queryClient = useQueryClient()

  const syncAccount = async (session: PersonalSession): Promise<void> => {
    await queryClient.invalidateQueries(
      { queryKey: ['auth', 'user', session.id], refetchType: 'all' },
      { throwOnError: true }
    )
    assertPersonalSession(session)
    const account = queryClient.getQueryState(['auth', 'user', session.id])
    if (account?.status !== 'success' || account.isInvalidated)
      throw new Error('Unable to refresh remaining spins.')
    gameService.completeSync(session)
    for (const queryKey of [
      ['stats', 'user', session.id],
      ['roulette', 'history', session.id],
      ['roulette', 'winners'],
      ['stats', 'prizePool'],
      ['stats', 'leaderboard'],
    ])
      void queryClient.invalidateQueries({ queryKey })
  }

  const mutation = useMutation({
    retry: false,
    mutationFn: async (action: SpinAction = 'new') => {
      if (gameService.isSpinning.value) return null
      const session = requirePersonalSession()
      try {
        const result = await gameService.execute(action, session)
        if (!isCurrentPersonalSession(session)) return null
        if (result || !gameService.pendingSpin.value) {
          // A failed refresh must not turn a confirmed win into a failed spin.
          try {
            await syncAccount(session)
          } catch {
            /* Keep syncRequired until an explicit refresh succeeds. */
          }
        }
        return result ? { spin: result, session } : null
      } catch (error) {
        if (!isCurrentPersonalSession(session)) return null
        if (!gameService.pendingSpin.value) {
          try {
            await syncAccount(session)
          } catch {
            /* Keep the button blocked on stale counts. */
          }
        }
        throw error
      } finally {
        gameService.finish(session)
      }
    },
  })

  return {
    spinAsync: mutation.mutateAsync,
    refreshAccount: async () => syncAccount(requirePersonalSession()),
  }
}
